import axios from "axios";

// A withdraw (Withdraw, or the one Revise and Regenerate start with)
// contractsapi is still running after its own wait: the proposal stays as it
// is until the vendor confirms, so the rep tries again later.
export class ReviseStillRunningError extends Error {
  constructor() {
    super("The signature request is still being withdrawn");
    this.name = "ReviseStillRunningError";
  }
}

// The stable codes a refused Revise, Regenerate or Withdraw carries: the
// proposal's own checks, and the e-sign connector's answers to the withdraw.
// Every other code, and a failure with none, keeps the generic message.
export enum ProposalReviseErrorCode {
  ALREADY_SIGNED = "ALREADY_SIGNED",
  NOT_SENT = "NOT_SENT",
  DEAL_CLOSED = "DEAL_CLOSED",
  // The proposal is on another deal's contract than the one it was opened from.
  WRONG_DEAL = "WRONG_DEAL",
  // Deleted, or never stored.
  PROPOSAL_NOT_FOUND = "PROPOSAL_NOT_FOUND",
  // Someone changed the proposal after it was opened.
  STALE_DRAFT = "STALE_DRAFT",
  // The request ended at the vendor first, signed or declined before the scan saw it.
  INVALID_REQUEST = "INVALID_REQUEST",
  NO_ESIGN_CONNECTION = "NO_ESIGN_CONNECTION",
  VENDOR_UNAVAILABLE = "VENDOR_UNAVAILABLE",
  // The vendor answered the withdraw with an error.
  VENDOR_ERROR = "VENDOR_ERROR",
  // The e-sign connector isn't answering.
  ESIGN_UNAVAILABLE = "ESIGN_UNAVAILABLE",
  // A withdraw still running, from another tab or after this one stopped asking.
  WITHDRAW_IN_PROGRESS = "WITHDRAW_IN_PROGRESS"
}

const KEY_PREFIX = "contracts.proposals.editor.reviseErrors";

const MESSAGE_KEY_BY_CODE: Record<ProposalReviseErrorCode, string> = {
  [ProposalReviseErrorCode.ALREADY_SIGNED]: `${KEY_PREFIX}.alreadySigned`,
  [ProposalReviseErrorCode.NOT_SENT]: `${KEY_PREFIX}.notSent`,
  [ProposalReviseErrorCode.DEAL_CLOSED]: `${KEY_PREFIX}.dealClosed`,
  [ProposalReviseErrorCode.WRONG_DEAL]: `${KEY_PREFIX}.wrongDeal`,
  [ProposalReviseErrorCode.PROPOSAL_NOT_FOUND]: `${KEY_PREFIX}.notFound`,
  [ProposalReviseErrorCode.STALE_DRAFT]: `${KEY_PREFIX}.stale`,
  [ProposalReviseErrorCode.INVALID_REQUEST]: `${KEY_PREFIX}.ended`,
  [ProposalReviseErrorCode.NO_ESIGN_CONNECTION]: `${KEY_PREFIX}.noConnection`,
  [ProposalReviseErrorCode.VENDOR_UNAVAILABLE]: `${KEY_PREFIX}.busy`,
  [ProposalReviseErrorCode.VENDOR_ERROR]: `${KEY_PREFIX}.vendorError`,
  [ProposalReviseErrorCode.ESIGN_UNAVAILABLE]: `${KEY_PREFIX}.busy`,
  [ProposalReviseErrorCode.WITHDRAW_IN_PROGRESS]: `${KEY_PREFIX}.stillWithdrawing`
};

const GENERIC_KEY = `${KEY_PREFIX}.failed`;

// Own-property only: `in` would accept inherited names like "toString".
const isKnownCode = (code: unknown): code is ProposalReviseErrorCode =>
  typeof code === "string" &&
  Object.prototype.hasOwnProperty.call(MESSAGE_KEY_BY_CODE, code);

const refusalCode = (error: unknown): unknown => {
  if (error instanceof ReviseStillRunningError) {
    return ProposalReviseErrorCode.WITHDRAW_IN_PROGRESS;
  }
  return axios.isAxiosError(error)
    ? (error.response?.data as { code?: unknown } | undefined)?.code
    : undefined;
};

// The i18n key for a Revise that didn't go through, read from the refusal's
// `code`. As for Send, the server's own `message` is not shown.
export const reviseErrorMessageKey = (error: unknown): string => {
  const code = refusalCode(error);
  return isKnownCode(code) ? MESSAGE_KEY_BY_CODE[code] : GENERIC_KEY;
};

const REGENERATE_KEY_PREFIX = "contracts.proposals.editor.regenerateErrors";

// Regenerate withdraws through Revise, so it gives the same reason, worded for
// Regenerate where the Revise message names Revise.
const REGENERATE_KEY_BY_CODE: Record<ProposalReviseErrorCode, string> = {
  ...MESSAGE_KEY_BY_CODE,
  [ProposalReviseErrorCode.ALREADY_SIGNED]: `${REGENERATE_KEY_PREFIX}.alreadySigned`,
  [ProposalReviseErrorCode.DEAL_CLOSED]: `${REGENERATE_KEY_PREFIX}.dealClosed`,
  [ProposalReviseErrorCode.WRONG_DEAL]: `${REGENERATE_KEY_PREFIX}.wrongDeal`,
  [ProposalReviseErrorCode.PROPOSAL_NOT_FOUND]: `${REGENERATE_KEY_PREFIX}.notFound`,
  [ProposalReviseErrorCode.STALE_DRAFT]: `${REGENERATE_KEY_PREFIX}.stale`,
  [ProposalReviseErrorCode.INVALID_REQUEST]: `${REGENERATE_KEY_PREFIX}.ended`,
  [ProposalReviseErrorCode.WITHDRAW_IN_PROGRESS]: `${REGENERATE_KEY_PREFIX}.stillWithdrawing`
};

const REGENERATE_GENERIC_KEY = "contracts.proposals.editor.regenerateError";

// The i18n key for a Regenerate whose withdraw didn't go through.
export const regenerateErrorMessageKey = (error: unknown): string => {
  const code = refusalCode(error);
  return isKnownCode(code)
    ? REGENERATE_KEY_BY_CODE[code]
    : REGENERATE_GENERIC_KEY;
};

const WITHDRAW_KEY_PREFIX = "contracts.proposals.editor.withdrawErrors";
// Only a withdraw refuses a proposal that isn't out for signature.
const NOT_OUT_FOR_SIGNATURE = "NOT_OUT_FOR_SIGNATURE";

// The codes a refused Withdraw carries: the Revise reason worded for Withdraw
// where the Revise message names Revise, the e-sign connector's as they are.
const WITHDRAW_KEY_BY_CODE: Record<string, string> = {
  [NOT_OUT_FOR_SIGNATURE]: `${WITHDRAW_KEY_PREFIX}.notOut`,
  [ProposalReviseErrorCode.ALREADY_SIGNED]: `${WITHDRAW_KEY_PREFIX}.alreadySigned`,
  [ProposalReviseErrorCode.WRONG_DEAL]: `${WITHDRAW_KEY_PREFIX}.wrongDeal`,
  [ProposalReviseErrorCode.PROPOSAL_NOT_FOUND]: `${WITHDRAW_KEY_PREFIX}.notFound`,
  [ProposalReviseErrorCode.STALE_DRAFT]: `${WITHDRAW_KEY_PREFIX}.stale`,
  [ProposalReviseErrorCode.INVALID_REQUEST]: `${WITHDRAW_KEY_PREFIX}.ended`,
  [ProposalReviseErrorCode.NO_ESIGN_CONNECTION]:
    MESSAGE_KEY_BY_CODE[ProposalReviseErrorCode.NO_ESIGN_CONNECTION],
  [ProposalReviseErrorCode.VENDOR_UNAVAILABLE]:
    MESSAGE_KEY_BY_CODE[ProposalReviseErrorCode.VENDOR_UNAVAILABLE],
  [ProposalReviseErrorCode.VENDOR_ERROR]:
    MESSAGE_KEY_BY_CODE[ProposalReviseErrorCode.VENDOR_ERROR],
  [ProposalReviseErrorCode.ESIGN_UNAVAILABLE]:
    MESSAGE_KEY_BY_CODE[ProposalReviseErrorCode.ESIGN_UNAVAILABLE],
  [ProposalReviseErrorCode.WITHDRAW_IN_PROGRESS]: `${WITHDRAW_KEY_PREFIX}.stillWithdrawing`
};

const WITHDRAW_GENERIC_KEY = "contracts.proposals.editor.withdrawError";

// The i18n key for a Withdraw that didn't go through.
export const withdrawErrorMessageKey = (error: unknown): string => {
  const code = refusalCode(error);
  return typeof code === "string" &&
    Object.prototype.hasOwnProperty.call(WITHDRAW_KEY_BY_CODE, code)
    ? WITHDRAW_KEY_BY_CODE[code]
    : WITHDRAW_GENERIC_KEY;
};
